<template>
  <div class="workspace-documents">
    <title-panel :display-button="false" :no-counter="true">
      {{ t('workspaceDocuments.title') }}
    </title-panel>
    <PVTabs v-model:value="activeEmbeddedTab" lazy>
      <PVTabList v-bind="compactTabsVariant.tabList">
        <router-link :to="getTabLink(WorkspaceDocumentsEmbeddedTab.EDITABLE)">
          <PVTab
            :value="WorkspaceDocumentsEmbeddedTab.EDITABLE"
            v-bind="compactTabsVariant.tab"
          >
            {{ t('workspaceDocuments.embeddedTabs.editable') }}
          </PVTab>
        </router-link>
        <router-link :to="getTabLink(WorkspaceDocumentsEmbeddedTab.READ_ONLY)">
          <PVTab
            :value="WorkspaceDocumentsEmbeddedTab.READ_ONLY"
            v-bind="compactTabsVariant.tab"
          >
            {{ t('workspaceDocuments.embeddedTabs.readOnly') }}
          </PVTab>
        </router-link>
      </PVTabList>
      <PVTabPanels v-bind="compactTabsVariant.tabPanels">
        <PVTabPanel :value="WorkspaceDocumentsEmbeddedTab.EDITABLE">
          <workspace-documents-editable-tab
            ref="workspaceDocumentsEditableTabRef"
            :workspace-id="props.workspaceId"
            @upload-document="onUploadDocumentClick"
            @add-for-editing="onAddForEditingClick"
          />
        </PVTabPanel>
        <PVTabPanel :value="WorkspaceDocumentsEmbeddedTab.READ_ONLY">
          <workspace-documents-read-only-tab
            ref="workspaceDocumentsReadOnlyTabRef"
            :workspace-id="props.workspaceId"
            @add-for-viewing="onAddForViewingClick"
          />
        </PVTabPanel>
      </PVTabPanels>
    </PVTabs>
    <workspace-content-type-select-modal
      ref="contentTypeModalRef"
      @content-type-selected="handleContentTypeSelected"
    />
    <workspace-document-picker-modal
      ref="documentPickerModalRef"
      :workspace-id="props.workspaceId"
      :workspace-location="props.workspaceLocation"
      :mode="documentPickerMode"
      :on-confirm="handleDocumentsSelected"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { RouteLocationRaw, useRoute, useRouter } from 'vue-router';
  import { IContentTypeNameAndId, WorkspaceDocumentPickerMode } from 'types';
  import { compactTabsVariant } from '@/primeVue';
  import WorkspaceContentTypeSelectModal from '@/components/WorkspaceContentTypeSelectModal/WorkspaceContentTypeSelectModal.vue';
  import WorkspaceDocumentPickerModal from '@/components/WorkspaceDocumentPicker/WorkspaceDocumentPickerModal.vue';
  import WorkspaceDocumentsEditableTab from './WorkspaceDocumentsEditableTab.vue';
  import WorkspaceDocumentsReadOnlyTab from './WorkspaceDocumentsReadOnlyTab.vue';
  import { useWorkspaceDocumentSelection } from '@/composables/api/workspaces';
  import { useTabs } from '@/composables/useTabs';
  import { Route, routeParams } from '@/routes';
  import { WorkspaceDocumentsEmbeddedTab } from './WorkspaceDocuments.types';

  const props = defineProps<{
    workspaceId: string;
    workspaceLocation: string;
  }>();

  const { t } = useI18n();
  const router = useRouter();
  const route = useRoute();

  const contentTypeModalRef = ref<InstanceType<
    typeof WorkspaceContentTypeSelectModal
  > | null>(null);
  const documentPickerModalRef = ref<InstanceType<
    typeof WorkspaceDocumentPickerModal
  > | null>(null);
  const documentPickerMode = ref<WorkspaceDocumentPickerMode>(
    WorkspaceDocumentPickerMode.EDIT,
  );
  const workspaceDocumentsEditableTabRef = ref<InstanceType<
    typeof WorkspaceDocumentsEditableTab
  > | null>(null);
  const workspaceDocumentsReadOnlyTabRef = ref<InstanceType<
    typeof WorkspaceDocumentsReadOnlyTab
  > | null>(null);

  const documentsTabQueryKey =
    routeParams[Route.WORKSPACE_DETAILS].query.documentsTab;

  const { activeTab: activeEmbeddedTab } = useTabs(
    Route.WORKSPACE_DETAILS,
    WorkspaceDocumentsEmbeddedTab,
    WorkspaceDocumentsEmbeddedTab.EDITABLE,
    {
      tabParam: documentsTabQueryKey,
    },
  );

  function getTabLink(tab: WorkspaceDocumentsEmbeddedTab): RouteLocationRaw {
    return {
      name: Route.WORKSPACE_DETAILS,
      params: {
        ...route.params,
      },
      query: {
        ...route.query,
        [documentsTabQueryKey]: tab,
      },
    };
  }

  const { handleDocumentsSelected } = useWorkspaceDocumentSelection(
    props.workspaceId,
    {
      onWorkingCopiesCreated: async () => {
        await workspaceDocumentsEditableTabRef.value?.refreshDocuments();
      },
      onReadOnlyAdded: async () => {
        await workspaceDocumentsEditableTabRef.value?.refreshDocuments();
        await workspaceDocumentsReadOnlyTabRef.value?.refreshDocuments();
      },
    },
  );

  function onUploadDocumentClick(): void {
    contentTypeModalRef.value?.show();
  }

  function onAddForEditingClick(): void {
    documentPickerMode.value = WorkspaceDocumentPickerMode.EDIT;
    documentPickerModalRef.value?.show();
  }

  function onAddForViewingClick(): void {
    documentPickerMode.value = WorkspaceDocumentPickerMode.VIEW;
    documentPickerModalRef.value?.show();
  }

  function handleContentTypeSelected(contentType: IContentTypeNameAndId): void {
    router.push({
      name: Route.WORKSPACE_SINGLE_UPLOAD,
      params: {
        [routeParams[Route.WORKSPACE_SINGLE_UPLOAD].workspaceId]:
          props.workspaceId,
        [routeParams[Route.WORKSPACE_SINGLE_UPLOAD].contentTypeId]:
          contentType.id,
      },
    });
  }
</script>

<style scoped lang="scss">
  .title-panel {
    margin-top: $tab-view-margin-top;
  }
</style>
